import { useEffect, useState } from "react";
import { ScrollText, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { api, fmtKES } from "@/lib/api";
import GlobalBackButton from "@/components/GlobalBackButton";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";

/**
 * Obondo Autopilot audit log — full history of Obondo-placed trades with
 * confidence, source (semi_auto | autonomous), SL/TP, and close reason.
 *
 * Answers the "why did Obondo do that?" question after autonomous mode
 * has been running.
 */
export default function AutopilotAuditLog() {
    const [data, setData] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/obondo/autopilot/audit-log?limit=50");
                setData(r.data);
            } catch {
                setData({ entries: [], summary: {} });
            }
        })();
    }, []);

    if (!data) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
        );
    }

    const { entries, summary } = data;
    const totalPnl = summary?.total_pnl ?? 0;

    return (
        <div className="min-h-screen bg-background text-foreground p-4 sm:p-6" data-testid="autopilot-audit-log">
            <GlobalBackButton />
            <div className="max-w-6xl mx-auto space-y-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <ScrollText className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                        <div className="overline text-primary">Obondo Autopilot · Audit log</div>
                        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                            What Obondo did, when, and why
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Every trade Obondo placed on your behalf — including the confidence, the source
                            (semi-auto approval vs fully-autonomous), and the reason it closed.
                        </p>
                    </div>
                </div>

                {/* Summary strip */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <SmallKpi label="Total trades"    value={summary.total ?? 0} />
                    <SmallKpi label="Open"            value={summary.open ?? 0} />
                    <SmallKpi label="Closed"          value={summary.closed ?? 0} />
                    <SmallKpi label="Wins"            value={`${summary.wins ?? 0} · ${summary.losses ?? 0}L`} />
                    <SmallKpi label="Cumulative PnL"  value={fmtKES(totalPnl)}
                        accent={toneClassName(signTone(totalPnl))}
                        icon={signTone(totalPnl) === "neg" ? <TrendingDown /> : <TrendingUp />} />
                </div>

                {/* Log */}
                <div className="border border-border bg-card rounded-lg p-4">
                    {entries.length === 0 ? (
                        <div className="py-12 text-center text-sm text-muted-foreground">
                            No Obondo trades yet. Enable semi-auto or autonomous mode on the Terminal and
                            Obondo will start placing trades — they'll all appear here.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid="audit-log-table">
                                <thead className="border-b border-border">
                                    <tr className="text-left text-muted-foreground">
                                        <th className="py-2 pr-2">Entry</th>
                                        <th className="pr-2">Exit</th>
                                        <th className="pr-2">Sym</th>
                                        <th className="pr-2">Side</th>
                                        <th className="pr-2 text-right">Qty</th>
                                        <th className="pr-2 text-right">Entry px</th>
                                        <th className="pr-2 text-right">Exit px</th>
                                        <th className="pr-2 text-right">SL / TP</th>
                                        <th className="pr-2 text-right">Conf</th>
                                        <th className="pr-2">Source</th>
                                        <th className="pr-2 text-right">PnL</th>
                                        <th>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((e) => (
                                        <tr key={e.id} className="border-b border-border/50 last:border-0" data-testid={`audit-row-${e.id}`}>
                                            <td className="py-1.5 pr-2 mono">{e.entry_ts?.slice(0, 16).replace("T", " ")}</td>
                                            <td className="pr-2 mono">{e.exit_ts?.slice(0, 16).replace("T", " ") || "—"}</td>
                                            <td className="pr-2 font-bold">{e.symbol}</td>
                                            <td className={`pr-2 font-semibold ${e.side === "buy" ? "text-emerald-500" : "text-red-500"}`}>
                                                {e.side?.toUpperCase()}
                                            </td>
                                            <td className="pr-2 text-right mono">{e.qty}</td>
                                            <td className="pr-2 text-right mono">{e.entry_price}</td>
                                            <td className="pr-2 text-right mono">{e.exit_price ?? "—"}</td>
                                            <td className="pr-2 text-right mono text-muted-foreground">
                                                {e.stop_loss ?? "—"}/{e.take_profit ?? "—"}
                                            </td>
                                            <td className="pr-2 text-right mono">{e.confidence ? `${Math.round(e.confidence * 100)}%` : "—"}</td>
                                            <td className="pr-2">
                                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                                    e.source === "autonomous"
                                                        ? "bg-primary/15 text-primary"
                                                        : "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                                                }`}>
                                                    {e.source || "?"}
                                                </span>
                                            </td>
                                            <td className={`pr-2 text-right mono font-semibold ${toneClassName(signTone(e.pnl_kes ?? 0))}`}>
                                                {e.pnl_kes != null ? `${signedPrefix(e.pnl_kes)}${e.pnl_kes.toLocaleString()}` : "—"}
                                            </td>
                                            <td className="text-muted-foreground">{e.close_reason || (e.status === "open" ? "still open" : "—")}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function SmallKpi({ label, value, sub, accent, icon }) {
    return (
        <div className="border border-border bg-card rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                {icon && <span className="w-3 h-3">{icon}</span>}
                {label}
            </div>
            <div className={`text-base sm:text-xl font-extrabold mono mt-1 ${accent || ""}`}>{value}</div>
            {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    );
}
